import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Color, Label, SingleDataSet } from 'ng2-charts';
import { AppConfig } from 'src/app/configs/app.config';
import { PriorityChartMapperService } from '../priority-chart-mapper/priority-chart-mapper.service';
import { ChartData } from './interfaces/chart-data';
import { ChartNg2Data } from './interfaces/chart-data-ng2';

@Injectable({
  providedIn: 'root'
})
export class ChartService {
  chartLabelClasses = AppConfig.ChartColorLabelClassesMapping;

  constructor(
    private translateService: TranslateService,
    private priorityChartMapperService: PriorityChartMapperService
  ) {}

  async metricsToNg2Chart(metrics: Record<string, Record<string, number>>): Promise<Record<string, ChartNg2Data>> {
    let chartData: Record<string, ChartData[]> = {};

    for (const categoryMetricKey in this.getMetricsWithoutActivities(metrics)) {
      if (metrics[categoryMetricKey]) {
        const metric = metrics[categoryMetricKey];

        chartData = await this.getChartDataByMetric(categoryMetricKey, metric as Record<string, number>, chartData);
      }
    }

    chartData = await this.combineAllMetricsWithPriorityMetrics(chartData, metrics);

    return this.chartDataToNg2ChartData(chartData, metrics);
  }

  public applyLabelClass(hexColor: string): string {
    return this.chartLabelClasses[hexColor.toLocaleLowerCase()];
  }

  private chartDataToNg2ChartData(
    chartData: Record<string, ChartData[]>,
    metrics?: Record<string, Record<string, number>>
  ): Record<string, ChartNg2Data> {
    const chartDataNg2: Record<string, { labels: Label[]; colors: Color[]; data: SingleDataSet }> = {};

    for (const categoryMetricKey in metrics) {
      if (chartData[categoryMetricKey]) {
        const categoryChartData = chartData[categoryMetricKey];

        chartDataNg2[categoryMetricKey] = {
          colors: [
            {
              backgroundColor: categoryChartData.map(t => t.backgroundColor)
            }
          ],
          labels: categoryChartData.map(t => t.label),
          data: categoryChartData.map(t => t.value)
        };
      }
    }

    return chartDataNg2;
  }

  private async getChartDataByMetric(
    categoryMetricKey: string,
    metric: Record<string, number>,
    chartData: Record<string, ChartData[]>
  ): Promise<Record<string, ChartData[]>> {
    for (const metricKey in metric) {
      if (Object.prototype.hasOwnProperty.call(metric, metricKey)) {
        const value = metric[metricKey];
        const metricMetaData = AppConfig.MetricColorTranslationMapping[metricKey];

        if (!metricMetaData) {
          continue;
        }

        if (!Array.isArray(chartData[categoryMetricKey])) {
          chartData[categoryMetricKey] = [];
        }

        const label = await this.translateService.get(metricMetaData.translationkey).toPromise();

        if (value) {
          chartData[categoryMetricKey].push({
            label,
            value,
            backgroundColor: metricMetaData.backgroundColor
          });
        }
      }
    }

    return chartData;
  }

  private getChartDataByPriority(
    metrics: Record<string, Record<string, number>>,
    priorityChartData: Record<string, ChartData>
  ): Record<string, ChartData[]> {
    const chartData: Record<string, ChartData[]> = { activities: [] };

    for (const priorityMetricName in metrics.activities) {
      if (priorityChartData[priorityMetricName]) {
        const priorityMetric = priorityChartData[priorityMetricName];
        const value = metrics.activities[priorityMetricName];

        if (value) {
          priorityMetric.value = value;
          chartData.activities.push(priorityMetric);
        }
      }
    }

    return chartData;
  }

  private getMetricsWithoutActivities(
    metrics: Record<string, Record<string, number>>
  ): Record<string, Record<string, number>> {
    const metricsCloned = { ...metrics };

    delete metricsCloned.activities;

    return metricsCloned;
  }

  private async combineAllMetricsWithPriorityMetrics(
    chartData: Record<string, ChartData[]>,
    metrics: Record<string, Record<string, number>>
  ): Promise<Record<string, ChartData[]>> {
    const priorityChartData = await this.priorityChartMapperService.getPriorityChartData();

    let chartDataCloned = { ...chartData };

    chartDataCloned = { ...this.getChartDataByPriority(metrics, priorityChartData), ...chartDataCloned };

    return chartDataCloned;
  }
}
